import { api } from "@/lib/apiClient";

export type MerosZustand = "oben" | "unten";

export interface MerosEintrag {
  id: number;
  zustand: MerosZustand;
  erfasst_am: string | null;
  notiz: string;
  erfasst_von: string;
}

export interface MerosAktuell {
  zustand: MerosZustand;
  seit: string | null;
  art: "gesetzt" | "klapp";
  notiz: string;
  erfasst_von: string;
}

export interface MerosVerlaufSegment {
  von: string;
  bis: string | null;
  zustand: MerosZustand;
  art: "gesetzt" | "klapp";
  quelle_id: number;
  notiz: string;
}

export interface MerosKlappTermin {
  id: number;
  klapp_am: string;
  notiz: string;
  erfasst_von: string;
  zustand_davor: MerosZustand | null;
  zustand_danach: MerosZustand | null;
  color?: string | null;
}

export interface MerosFarben {
  oben: string;
  unten: string;
}

export interface MerosPlanBar {
  startIdx: number;
  span: number;
  zustand: MerosZustand;
  art: "gesetzt" | "klapp";
  notiz: string;
  von: string;
}

export interface MerosBedarf {
  mail_id: number;
  event_title: string;
  event_date: string;
  venue: string;
  rp_nummer: string;
  bereich: string;
  ort: string;
  kategorie: string;
  datum: string;
  von: string | null;
  bis: string | null;
  aufgabe: string;
  notiz: string;
  zustand: MerosZustand | null;
}

export interface MerosUebersicht {
  aktuell: MerosAktuell | null;
  historie: MerosEintrag[];
  bedarf: MerosBedarf[];
  verlauf: MerosVerlaufSegment[];
  klapp_termine: MerosKlappTermin[];
  farben?: MerosFarben;
}

const MEROS_RE = /merost/i;
const UNTEN_RE = /\bunten\b|runter/i;
const OBEN_RE = /\boben\b|\bhochklapp|\bhoch\b|\brauf\b/i;

function richtung(text: string): MerosZustand | null {
  const unten = UNTEN_RE.exec(text);
  const oben = OBEN_RE.exec(text);
  if (unten && !oben) return "unten";
  if (oben && !unten) return "oben";
  if (unten && oben) return unten.index <= oben.index ? "unten" : "oben";
  return null;
}

/** Dieselbe Lesart wie in der API: Aufgabe zuerst, Notiz nur ohne Richtung in der Aufgabe. */
export function merosZustandAusText(
  aufgabe?: string | null,
  notiz?: string | null,
): MerosZustand | null {
  const task = (aufgabe || "").trim();
  if (MEROS_RE.test(task)) {
    const ausAufgabe = richtung(task);
    if (ausAufgabe) return ausAufgabe;
  }
  const text = `${aufgabe || ""} ${notiz || ""}`.trim();
  if (!text || !MEROS_RE.test(text)) return null;
  return richtung(text);
}

export function einsatzMerosZustand(einsatz: {
  aufgabe?: string | null;
  notiz?: string | null;
  meros_zustand?: string | null;
}): MerosZustand | null {
  if (einsatz.meros_zustand === "oben" || einsatz.meros_zustand === "unten") {
    return einsatz.meros_zustand;
  }
  return merosZustandAusText(einsatz.aufgabe, einsatz.notiz);
}

export function merosZustandLabel(zustand: MerosZustand | null | undefined): string {
  if (zustand === "oben") return "OBEN";
  if (zustand === "unten") return "UNTEN";
  return "unklar";
}

export function heuteIso(now = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function bedarfAnstehend(
  item: { von: string | null; bis: string | null },
  heute = heuteIso(),
): boolean {
  const ende = item.bis || item.von;
  if (!ende) return true;
  return ende >= heute;
}

/** Balken für die Meros-Zeile: an jedem Tag gilt der Zustand am Tagesende. */
export function merosPlanBars(segments: MerosVerlaufSegment[], days: Date[]): MerosPlanBar[] {
  const bars: MerosPlanBar[] = [];
  days.forEach((day, index) => {
    const start = new Date(day);
    start.setHours(0, 0, 0, 0);
    const end = new Date(day);
    end.setHours(23, 59, 59, 999);
    let segment: MerosVerlaufSegment | null = null;
    for (const item of segments) {
      const von = new Date(item.von).getTime();
      if (Number.isNaN(von) || von > end.getTime()) continue;
      if (item.bis) {
        const bis = new Date(item.bis).getTime();
        if (!Number.isNaN(bis) && bis <= start.getTime()) continue;
      }
      segment = item;
    }
    if (!segment) return;
    const previous = bars[bars.length - 1];
    if (previous && previous.zustand === segment.zustand && previous.startIdx + previous.span === index) {
      previous.span += 1;
      return;
    }
    bars.push({
      startIdx: index,
      span: 1,
      zustand: segment.zustand,
      art: segment.art,
      notiz: segment.notiz,
      von: segment.von,
    });
  });
  return bars;
}

export function getMerosUebersicht() {
  return api.get<MerosUebersicht>("/veranstaltungen/meros");
}

export function getMerosVerlauf() {
  return api.get<{ verlauf: MerosVerlaufSegment[]; farbe_oben?: string; farbe_unten?: string }>(
    "/montageplanung/meros-verlauf",
  );
}

export function legeMerosKlappTermin(payload: { klapp_am: string; notiz?: string; color?: string }) {
  return api.post<MerosUebersicht>("/veranstaltungen/meros/klapp", payload);
}

export function loescheMerosKlappTermin(id: number) {
  return api.del<MerosUebersicht>(`/veranstaltungen/meros/klapp/${id}`);
}

export function aendereMerosKlappFarbe(id: number, color: string) {
  return api.patch<MerosUebersicht>(`/veranstaltungen/meros/klapp/${id}`, { color });
}

export function speichereMerosFarben(farben: MerosFarben) {
  return api.put<MerosUebersicht>("/veranstaltungen/meros/farben", {
    farbe_oben: farben.oben,
    farbe_unten: farben.unten,
  });
}

export function erfasseMerosZustand(payload: {
  zustand: MerosZustand;
  erfasst_am?: string;
  notiz?: string;
}) {
  return api.post<MerosUebersicht>("/veranstaltungen/meros", payload);
}
